import { useMemo, useState } from 'react';

import { Icon } from '@components';
import { Permissions } from 'interfaces/Permissions';

type Resource = {
  [context: string]: string[];
};

type Props = {
  value: Permissions;
  onChange: (value: Permissions, resourceSelected: string) => void;
  permissions: Resource;
};

const defaultPermissions = ['Ler', 'Criar', 'Editar', 'Excluir', 'Ordenar'];

export function checkIfAllIsTrue(
  permissions: Permissions | any[],
  resourcesList: Resource
) {
  const allPerms: { Todos: { [context: string]: boolean } } = {
    Todos: {},
  };

  if (Array.isArray(permissions) && permissions.length === 0) {
    return allPerms;
  }

  const merged = mergePermission(resourcesList, permissions as Permissions);

  defaultPermissions.forEach((perm) => {
    const allPermitionsAreTrue = Object.values(merged)
      .map((permName) => permName[perm])
      .every((it) => it || typeof it === 'undefined');

    allPerms.Todos[perm] = allPermitionsAreTrue;
  });

  return allPerms;
}

// Função para realizar o entre lista de recursos e as permissões
function mergePermission(resource: Resource, perms: Permissions) {
  const merged: any = {};

  for (const resourceKey in resource) {
    if (!merged[resourceKey]) {
      merged[resourceKey] = {};
    }

    for (const resourceValue of resource[resourceKey]) {
      if (!merged[resourceKey][resourceValue]) {
        merged[resourceKey][resourceValue] = false;
      }
    }

    merged[resourceKey] = {
      ...merged[resourceKey],
      ...perms[resourceKey],
    };
  }

  return merged as Permissions;
}

function updatePermissionsByContext(
  permissions: Permissions,
  context: string
): Permissions {
  const contextToPermissions: { [key: string]: string[] } = {
    Dashboard: ['Templates de budget'],
    Budgets: ['Templates de budget', 'Centro de custo'],
    Categorias: ['Centro de custo', 'Usuários'],
    'Centro de custo': ['Usuários'],
    'Centro de custo de/para': ['Centro de custo'],
  };

  let updatedPermissions = { ...permissions };

  const relatedPermissions = contextToPermissions[context];

  if (relatedPermissions) {
    relatedPermissions.forEach((resource) => {
      if (permissions[context] && permissions[context]['Ler']) {
        updatedPermissions = {
          ...updatedPermissions,
          [resource]: {
            ...updatedPermissions[resource],
            Ler: permissions[context]['Ler'],
          },
        };
      }
    });
  }

  return updatedPermissions;
}

const resourceColumns = (
  value: Permissions,
  onChange: (value: Permissions, resource: string, context: string) => void,
  context: string,
  resource?: string,
  label?: string
) => {
  if (!resource) return <></>;

  return (
    <div
      key={`permission-${context}-${resource}`}
      className="form-check"
      style={{ placeContent: 'center' }}
    >
      <input
        name={`permission-${context}-${resource}`}
        id={`permission-${context}-${resource}`}
        onChange={(e) => {
          const checked = e.target.checked;
          onChange(
            {
              ...value,
              [context]: {
                ...value[context],
                [resource]: checked,
              },
            },
            resource,
            context
          );
        }}
        checked={value?.[context]?.[resource] ?? false}
        className="form-check-input"
        style={{ borderColor: '#a5b7c0' }}
        type="checkbox"
      />
      {label && (
        <label
          className="form-check-label color-primary"
          htmlFor={`permission-${context}-${resource}`}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export function PermissionRow({
  context,
  resources,
  value,
  onChange,
  index,
}: {
  context: string;
  resources: string[];
  value: Permissions;
  onChange: (value: Permissions, resource: string, context: string) => void;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const morePermission = useMemo(
    () => resources.filter((it) => !defaultPermissions.includes(it)),
    [resources]
  );

  const shouldShowExpandMore = useMemo(() => {
    return morePermission.length;
  }, [morePermission]);

  const classNames = [];

  if (shouldShowExpandMore) {
    classNames.push('pointer');
  }

  return (
    <>
      <tr
        key={`permission-context-${context}-${index}-${Math.random()}`}
        className="color-primary"
        style={{
          borderBottom: '1px solid #F0F3F5',
        }}
      >
        <td
          className={classNames.join(' ')}
          style={{ fontWeight: 'bold', backgroundColor: '#F0F3F5' }}
          onClick={() => {
            if (shouldShowExpandMore) setIsOpen((p) => !p);
          }}
        >
          {context}
        </td>
        {defaultPermissions.map((perms, i) => (
          <td key={`permission-context-${context}-${perms}-${Math.random()}`}>
            {resourceColumns(
              value,
              onChange,
              context,
              resources.find((it) => it === perms)
            )}
          </td>
        ))}
        <td
          style={{ textAlign: 'center' }}
          className={classNames.join(' ')}
          onClick={() => {
            if (shouldShowExpandMore) setIsOpen((p) => !p);
          }}
        >
          {!!shouldShowExpandMore &&
            (isOpen ? (
              <Icon children={'expand_less'} />
            ) : (
              <Icon children={'expand_more'} />
            ))}
        </td>
      </tr>
      {isOpen && (
        <tr className="isOpen">
          <td colSpan={7} className="color-primary">
            <h5>Ações extras</h5>
            <div
              className="mt-s-200 row"
              style={{
                gap: 16,
              }}
            >
              {morePermission.map((resource) =>
                resourceColumns(value, onChange, context, resource, resource)
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PermissionsInput(props: Props) {
  return (
    <>
      <div
        className="border-radius-100"
        style={{ maxHeight: 400, overflowY: 'auto' }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
          }}
          cellPadding={0}
          cellSpacing={0}
        >
          <thead
            className="divider-permission-input"
            style={{ position: 'sticky', top: 0, color: 'white', height: 40 }}
          >
            <tr className="bg-primary">
              <th style={{ width: '50%', paddingLeft: 8, textAlign: 'start' }}>
                Área
              </th>
              <th style={{ textAlign: 'center' }}>Ler</th>
              <th style={{ textAlign: 'center' }}>Criar</th>
              <th style={{ textAlign: 'center' }}>Editar</th>
              <th style={{ textAlign: 'center' }}>Excluir</th>
              <th style={{ textAlign: 'center' }}>Ordenar</th>
              <th style={{ width: '5%', textAlign: 'center' }} />
            </tr>
          </thead>
          <tbody className="divider-permission-input">
            <PermissionRow
              context={'Todos'}
              resources={defaultPermissions}
              value={{
                ...props.value,
                ...checkIfAllIsTrue(props.value, props.permissions),
              }}
              index={-10}
              onChange={(perms: Permissions, resourceChanged: string) => {
                const newPerms: Permissions = {};

                Object.entries(props.permissions).forEach(([permName]) => {
                  newPerms[permName] = {
                    ...props.value[permName],
                    [resourceChanged]: perms['Todos'][resourceChanged],
                  };
                });

                props?.onChange(
                  {
                    ...props.value,
                    ...newPerms,
                  },
                  resourceChanged
                );
              }}
            />
            {props.value &&
              Object.entries(props.permissions).map(
                ([context, resources], i) => (
                  <PermissionRow
                    key={context + i}
                    context={context}
                    resources={resources}
                    onChange={(
                      perms: Permissions,
                      resourceChanged: string,
                      context: string
                    ) => {
                      let permissions = {
                        ...perms,
                      };

                      permissions = updatePermissionsByContext(
                        permissions,
                        context
                      );

                      props?.onChange(
                        {
                          ...permissions,
                        },
                        resourceChanged
                      );
                    }}
                    value={props.value}
                    index={i + 1}
                  />
                )
              )}
          </tbody>
        </table>
      </div>
    </>
  );
}
